/**
 * Priority Calculator Utility
 *
 * This utility calculates lead priority based on LeadScore and other factors
 * LeadScore ranges from 0-100, Priority is calculated on frontend
 */

// Priority thresholds based on LeadScore (0-100)
export const PRIORITY_THRESHOLDS = {
  HOT: 85,     // 85-100: Hot leads - immediate attention required
  URGENT: 70,  // 70-84: Urgent - high priority leads  
  HIGH: 50,    // 50-69: High priority leads
  MEDIUM: 25,  // 25-49: Medium priority leads
  LOW: 0       // 0-24: Low priority leads
};

// Priority configuration matching the existing LEAD_PRIORITIES structure
export const CALCULATED_PRIORITIES = {
  HOT: { id: 'hot', label: 'Hot', color: 'red', score: 5, bgColor: 'bg-red-100 dark:bg-red-900/20', textColor: 'text-red-700 dark:text-red-300' },
  URGENT: { id: 'urgent', label: 'Urgent', color: 'red', score: 4, bgColor: 'bg-red-50 dark:bg-red-900/20', textColor: 'text-red-600 dark:text-red-400' },
  HIGH: { id: 'high', label: 'High', color: 'orange', score: 3, bgColor: 'bg-orange-50 dark:bg-orange-900/20', textColor: 'text-orange-700 dark:text-orange-300' },
  MEDIUM: { id: 'medium', label: 'Medium', color: 'yellow', score: 2, bgColor: 'bg-yellow-50 dark:bg-yellow-900/20', textColor: 'text-yellow-700 dark:text-yellow-300' },
  LOW: { id: 'low', label: 'Low', color: 'gray', score: 1, bgColor: 'bg-gray-50 dark:bg-gray-900/20', textColor: 'text-gray-700 dark:text-gray-300' },
  UNSCORED: { id: 'unscored', label: 'Unscored', color: 'gray', score: 0, bgColor: 'bg-gray-50 dark:bg-gray-900/20', textColor: 'text-gray-500 dark:text-gray-400' }
};

/**
 * Calculate priority based on LeadScore ONLY (ignore backend priority)
 * @param {number|string} leadScore - The lead score (0-100) or "Unscored"
 * @returns {Object} Priority configuration object
 */
export const calculatePriority = (leadScore) => {
  // Handle unscored leads
  if (leadScore === 'Unscored' || leadScore === null || leadScore === undefined) {
    return CALCULATED_PRIORITIES.UNSCORED;
  }

  // Convert to number if string
  const score = typeof leadScore === 'string' ? parseFloat(leadScore) : leadScore;

  // Handle invalid scores
  if (isNaN(score) || score < 0) {
    return CALCULATED_PRIORITIES.UNSCORED;
  }

  // Calculate priority based on score thresholds ONLY
  if (score >= PRIORITY_THRESHOLDS.HOT) {
    return CALCULATED_PRIORITIES.HOT;
  } else if (score >= PRIORITY_THRESHOLDS.URGENT) {
    return CALCULATED_PRIORITIES.URGENT;
  } else if (score >= PRIORITY_THRESHOLDS.HIGH) {
    return CALCULATED_PRIORITIES.HIGH;
  } else if (score >= PRIORITY_THRESHOLDS.MEDIUM) {
    return CALCULATED_PRIORITIES.MEDIUM;
  } else {
    return CALCULATED_PRIORITIES.LOW;
  }
};

/**
 * Get priority color for score display
 * @param {number} leadScore - The lead score (0-100)
 * @returns {string} CSS color class
 */
export const getScoreColor = (leadScore) => {
  const priority = calculatePriority(leadScore);

  switch (priority.id) {
    case 'hot':
      return 'text-red-600 dark:text-red-400';
    case 'urgent':
      return 'text-red-500 dark:text-red-400';
    case 'high':
      return 'text-orange-500 dark:text-orange-400';
    case 'medium':
      return 'text-yellow-500 dark:text-yellow-400';
    case 'low':
      return 'text-gray-500 dark:text-gray-400';
    default:
      return 'text-gray-400 dark:text-gray-500';
  }
};

/**
 * Get progress bar color for score display
 * @param {number} leadScore - The lead score (0-100)
 * @returns {string} CSS background color class
 */
export const getScoreProgressColor = (leadScore) => {
  const priority = calculatePriority(leadScore);

  switch (priority.id) {
    case 'hot':
      return 'bg-red-500';
    case 'urgent':
      return 'bg-red-400';
    case 'high':
      return 'bg-orange-500';
    case 'medium':
      return 'bg-yellow-500';
    case 'low':
      return 'bg-gray-500';
    default:
      return 'bg-gray-300';
  }
};

/**
 * Get score label based on score value
 * @param {number} leadScore - The lead score (0-100)
 * @returns {string} Human readable score label
 */
export const getScoreLabel = (leadScore) => {
  if (leadScore === 'Unscored' || leadScore === null || leadScore === undefined) {
    return 'Not Scored';
  }

  const score = typeof leadScore === 'string' ? parseFloat(leadScore) : leadScore;

  if (isNaN(score) || score < 0) {
    return 'Invalid Score';
  }

  if (score >= 85) return 'Excellent Lead';
  if (score >= 70) return 'Very Good Lead';
  if (score >= 50) return 'Good Lead';
  if (score >= 25) return 'Average Lead';
  return 'Needs Attention';
};

/**
 * Transform backend lead data to include calculated priority
 * @param {Object} lead - Raw lead data from backend
 * @returns {Object} Transformed lead data with calculated priority
 */
export const transformLeadWithPriority = (lead) => {
  if (!lead) return lead;

  // Calculate priority based ONLY on LeadScore, ignore any backend Priority field
  const calculatedPriority = calculatePriority(lead.LeadScore);

  let parsedLeadInfo = {};
  if (typeof lead.leadinfo === 'string') {
    try {
      const parsed = JSON.parse(lead.leadinfo);
      if (Array.isArray(parsed)) {
        parsedLeadInfo = parsed.length > 0 ? parsed[0] : {};
      } else if (parsed && typeof parsed === 'object') {
        parsedLeadInfo = parsed;
      }
    } catch (e) {
      console.error("Error parsing leadinfo", e);
    }
  }

  let parsedDynamicFields = {};

  const processDynamicFields = (fields) => {
    if (Array.isArray(fields)) {
      fields.forEach(field => {
        if (field.FieldName) {
          parsedDynamicFields[field.FieldName] = field.FieldValue;
          // Also add normalized versions: lowercase no spaces, and lowercase with underscores
          const normalizedKey = field.FieldName.toLowerCase().replace(/\s+/g, '');
          const underscoreKey = field.FieldName.toLowerCase().replace(/\s+/g, '_');
          parsedDynamicFields[normalizedKey] = field.FieldValue;
          parsedDynamicFields[underscoreKey] = field.FieldValue;
        }
      });
    }
  };

  if (typeof lead.DynamicFields === 'string') {
    try {
      const parsedArray = JSON.parse(lead.DynamicFields);
      processDynamicFields(parsedArray);
    } catch (e) {
      console.error("Error parsing DynamicFields", e);
    }
  } else if (Array.isArray(lead.DynamicFields)) {
    processDynamicFields(lead.DynamicFields);
  }

  let parsedNotes = null;
  const rawNotes = lead.Notes || lead.notes || parsedLeadInfo?.Notes || parsedLeadInfo?.notes;

  if (rawNotes) {
    if (typeof rawNotes === 'string' && rawNotes.trim()) {
      try {
        const parsed = JSON.parse(rawNotes);
        if (Array.isArray(parsed) && parsed.length > 0) {
          parsedNotes = parsed[0];
        } else if (parsed && typeof parsed === 'object') {
          parsedNotes = parsed;
        }
      } catch (e) {
        // Fallback: If it's a non-JSON string, treat the string itself as the note description
        parsedNotes = { NoteDescription: rawNotes };
      }
    } else if (typeof rawNotes === 'object' && rawNotes !== null) {
      if (Array.isArray(rawNotes) && rawNotes.length > 0) {
        parsedNotes = rawNotes[0];
      } else {
        parsedNotes = rawNotes;
      }
    }
  }

  // Final firstNote extraction with multiple property name fallbacks
  const firstNote = parsedNotes?.NoteDescription ||
    parsedNotes?.noteDescription ||
    parsedNotes?.Description ||
    parsedNotes?.description ||
    parsedNotes?.Note ||
    parsedNotes?.note ||
    "";

  // Extract root properties, falling back to parsed info
  const fullName = lead.FullName || parsedLeadInfo.FullName || '';
  const firstName = lead.FirstName || parsedLeadInfo.FirstName || '';
  const lastName = lead.LastName || parsedLeadInfo.LastName || '';
  const DisplayLeadId = lead.DisplayLeadId || parsedLeadInfo.DisplayLeadId || '';
  const email = lead.Email || parsedLeadInfo.Email || parsedDynamicFields.email || '';
  const phone = lead.PhoneNumber || parsedLeadInfo.PhoneNumber || parsedDynamicFields.phonenumber || lead.Phone || '';
  const stageName = lead.StageName || lead.stageName || parsedLeadInfo.StageName || parsedLeadInfo.stageName || '';
  const subStageName = lead.SubStageName || lead.subStageName || parsedLeadInfo.SubStageName || parsedLeadInfo.subStageName || '';
  const ownerName = lead.OwnerName || parsedLeadInfo.OwnerName || lead.AssignedToName || '';
  const leadSourceName = lead.LeadSourceName || parsedLeadInfo.LeadSourceName || lead.sourceName || lead.SourceName || parsedLeadInfo.SourceName || parsedLeadInfo.sourceName || '';

  return {
    ...lead,
    ...parsedLeadInfo,
    ...parsedDynamicFields,
    // Map backend fields to frontend expected structure
    // NOTE: lead.Priority is completely ignored - we calculate priority from LeadScore only
    id: lead.LeadId || lead.Id,
    DisplayLeadId: DisplayLeadId,
    name: fullName || `${firstName} ${lastName}`.trim() || 'Unknown',
    firstName: firstName,
    lastName: lastName,
    email: email,
    mobile: phone,
    phone: phone,
    city: lead.City,
    state: lead.State,
    country: lead.Country,
    zip: lead.Zip,
    gender: lead.Gender,
    dob: lead.DOB,
    leadScore: lead.LeadScore || 0,
    status: lead.Status,
    stage: stageName,
    stageName: stageName,
    subStage: subStageName,
    subStageName: subStageName,
    stageId: lead.StageId,
    subStageId: lead.SubStageId,
    leadSourceId: lead.LeadSourceId,
    assignedTo: lead.AssignedTo,
    assignedToName: ownerName,
    ownerName: ownerName,
    campaign: lead.Campaign,
    medium: lead.Medium,
    processed: lead.Processed,
    processedAt: lead.ProcessedAt,
    createdAt: lead.CreatedAt,
    modifiedAt: lead.ModifiedAt,
    modifiedBy: lead.ModifiedBy,
    // Calculated priority (based ONLY on LeadScore, backend Priority field is ignored)
    priority: calculatedPriority.id,
    priorityConfig: calculatedPriority,
    priorityLabel: calculatedPriority.label,
    priorityColor: calculatedPriority.color,
    // Score-based properties
    scoreLabel: getScoreLabel(lead.LeadScore),
    scoreColor: getScoreColor(lead.LeadScore),
    scoreProgressColor: getScoreProgressColor(lead.LeadScore),
    // Handled parsed notes for the Notes column
    firstNote: firstNote,
    LeadSourceName: leadSourceName,
  };
};

/**
 * Transform array of backend leads
 * @param {Array} leads - Array of raw lead data from backend
 * @returns {Array} Array of transformed lead data
 */
export const transformLeadsArray = (leads) => {
  if (!Array.isArray(leads)) return [];
  return leads.map(transformLeadWithPriority);
};

export default {
  calculatePriority,
  getScoreColor,
  getScoreProgressColor,
  getScoreLabel,
  transformLeadWithPriority,
  transformLeadsArray,
  PRIORITY_THRESHOLDS,
  CALCULATED_PRIORITIES
};

